/**
 * @file src/room/roomInvite.js
 * @description The link that invites someone into this room, and the creator-only facts that ride
 * beside it on the boot URL.
 *
 * One owner, because there are two readers with no tolerance for drift: the mint sends this URL to
 * the relay as the room's canonical entry point, and the waiting room puts the same string in front
 * of the person doing the inviting. If those two ever disagreed, the link people were handed would
 * not be the link the room believes in.
 *
 * An invite is simply this tab's URL with the CREATOR-ONLY params removed. Everything else — the
 * collection, the room id, the grant, the deck and player counts — is exactly what a joiner needs.
 */

/**
 * Params that describe the creator's arrival rather than the room, and so must never survive into a
 * link handed to someone else:
 *  - `create` mints the room. A joiner carrying it would try to mint a room that already exists.
 *  - `skipped` counts what the creator's own access left out of the roster. It is a statement about
 *    THEIR permissions; on anyone else's screen it would be a number about a stranger.
 */
const CREATOR_ONLY_PARAMS = ['create', 'skipped'];

/**
 * Params that are simply obsolete, and are dropped for a different reason: not because they describe
 * the creator, but because they describe nothing at all any more.
 *  - `code` used to carry the room's sayable name. It travels with the GRANT now, so a `code` on a
 *    URL is either stale or hand-written, and passing one along would put a name on an invite that
 *    the room it points at does not answer to.
 */
const OBSOLETE_PARAMS = ['code'];

/**
 * The canonical invite URL for the room this tab is in — this tab's URL minus the creator-only
 * params. Returns the raw href if it cannot be parsed (never null: a slightly wrong link the viewer
 * can see and correct beats an empty box), and null when there is no window at all.
 * @returns {string|null}
 */
export function getRoomInviteUrl() {
  if (typeof window === 'undefined') return null;
  const href = window.location?.href ?? null;
  if (!href) return null;
  try {
    const url = new URL(href);
    for (const key of [...CREATOR_ONLY_PARAMS, ...OBSOLETE_PARAMS]) url.searchParams.delete(key);
    return url.toString();
  } catch {
    return href;
  }
}

/**
 * Where a join CODE is used: the website's playground, in the reader's language.
 *
 * This is the second half of the invitation, and it exists because a code has to be typed
 * somewhere. It is deliberately NOT the room's own link — it points at a page that keeps working
 * after the room has died, which a room link does not, and it carries no grant, so it is safe to
 * paint onto something public.
 *
 * Read from `VITE_ROOT_BASE` and nothing else. `Constants.ROOT_BASE` would be the obvious source and
 * is the wrong one here: it falls back to `window.location.origin`, which for this app is the
 * ORBITER's origin, not the site's — so an unset variable would not fail, it would quietly paint
 * `orbiter.plantasia.space/en/playground` onto a public image, and that path does not exist. A
 * missing origin has to come back null so the card is drawn without a link at all.
 *
 * @param {string} lang Language tag for the locale segment.
 * @returns {string|null} Absolute URL, or null when the site's origin is not configured.
 */
export function getPlaygroundUrl(lang) {
  let configured;
  try {
    configured = import.meta.env?.VITE_ROOT_BASE;
  } catch {
    configured = undefined;
  }
  const base = String(configured || '').replace(/\/+$/, '');
  if (!base) return null;
  // Only the language part: the site's routes are `/en`, `/es`, never `/en-GB`. Trimmed first,
  // because whitespace is truthy — `'  '` would otherwise survive as a locale segment and mint a
  // route with a space in it.
  const locale = String(lang || '').trim().split('-')[0].toLowerCase() || 'en';
  return `${base}/${locale}/playground`;
}

/**
 * How many roster items the mint left out because the creator could not see them (`?skipped=`),
 * or 0 when nothing was skipped / this tab is not the creator's.
 *
 * The website mints the grant and learns this, then hands the creator straight into the room — so
 * the number has to travel on the URL to be told to the one person it is about. A junk or negative
 * value reads as zero rather than rendering "-3 items aren't included".
 * @returns {number}
 */
export function getRoomSkippedCount() {
  if (typeof window === 'undefined') return 0;
  try {
    const raw = new URLSearchParams(window.location?.search ?? '').get('skipped');
    const n = Number.parseInt((raw || '').trim(), 10);
    return Number.isInteger(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}
